import React, { useState, useRef, useEffect, useContext } from 'react';
import { ChatContext } from '../contexts/ChatContext';
import ChartRenderer from '../ChartRenderer';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import ExportJsonExcel from 'js-export-excel';
import { Send } from 'react-bootstrap-icons';
import RespuestaMarkdown from './RespuestaMarkdown';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    fontFamily: 'Arial, sans-serif'
  },
  header: {
    padding: '10px 16px',
    borderBottom: '1px solid #eee',
    fontWeight: 'bold'
  },
  messagesArea: {
    flex: 1,
    minHeight: 0,
    padding: '16px',
    overflowY: 'auto',
    backgroundColor: '#f9f9f9'
  },
  inputArea: {
    padding: '10px',
    borderTop: '1px solid #eee',
    display: 'flex'
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginRight: '5px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    resize: 'none',
    minHeight: '38px',
    maxHeight: '120px',
    overflowY: 'auto'
  },
  msgUser: {
    backgroundColor: '#e7f1ff',
    padding: '8px',
    borderRadius: '10px 10px 0 10px',
    marginBottom: '8px',
    alignSelf: 'flex-end',
    maxWidth: '80%',
    marginLeft: 'auto'
  },
  msgBot: {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    padding: '8px',
    borderRadius: '10px 10px 10px 0',
    marginBottom: '8px',
    maxWidth: '95%'
  }
};

// Misma lógica de exportación que ChatWidget.js (widget flotante) — se mantiene duplicada a
// propósito para no arriesgar el comportamiento del widget ya probado.
const cleanDataForExport = (data) => {
  if (!Array.isArray(data)) return data;
  return data.map(row => {
    const cleanRow = {};
    for (const [key, value] of Object.entries(row)) {
      if (key === '_id') continue;
      if (value === null || value === undefined || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      if (typeof value === 'object' && !Array.isArray(value)) {
        const keys = Object.keys(value);
        if (keys.includes('_bsontype') || keys.includes('$oid')) continue;
      }
      cleanRow[key] = value;
    }
    return cleanRow;
  });
};

const downloadExcel = (data, filename = 'datos') => {
  try {
    const cleaned = cleanDataForExport(Array.isArray(data) ? data : [data]);
    if (!cleaned || cleaned.length === 0) {
      alert('No hay datos disponibles para exportar');
      return;
    }
    const headers = Object.keys(cleaned[0]);
    const option = {
      fileName: filename,
      datas: [
        {
          sheetData: cleaned,
          sheetName: filename,
          sheetFilter: headers,
          sheetHeader: headers,
        },
      ],
    };
    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  } catch (error) {
    console.error('Error generando Excel:', error);
    alert('Error al generar el archivo Excel');
  }
};

const downloadPDF = (messageContent, rows, filename = 'reporte') => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let y = 20;

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(`Reporte ${process.env.REACT_APP_NOMBRE_INSTITUCION || 'Asistente IA'}`, 14, y);
    y += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Fecha: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    if (messageContent) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Respuesta:', 14, y);
      y += 8;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      const splitText = doc.splitTextToSize(messageContent, pageWidth - 28);
      doc.text(splitText, 14, y);
      y += (splitText.length * 5) + 10;
    }

    const cleaned = cleanDataForExport((rows || []).slice(0, 50));
    if (cleaned && cleaned.length > 0) {
      const headers = Object.keys(cleaned[0]);
      const body = cleaned.map(row => headers.map(h => row[h] ?? ''));
      doc.autoTable({
        head: [headers],
        body,
        startY: y,
        margin: { left: 14, right: 14 },
        styles: { fontSize: 8 },
        headStyles: { fillColor: [13, 110, 253] }
      });
    }

    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`${filename}_${timestamp}.pdf`);
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el archivo PDF');
  }
};

/**
 * UI de mensajes + input, compartida con el widget flotante (ChatWidget.js) vía el mismo
 * ChatContext. Pensada para ocupar el 100% de alto del contenedor donde se monte (página
 * dedicada de Asistente IA).
 */
export default function ChatPanel() {
  const { messages, isLoading, loadingConv, enviar } = useContext(ChatContext);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isLoading && inputRef.current) inputRef.current.focus();
  }, [isLoading]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const currentInput = input;
    setInput('');
    await enviar(currentInput);
  };

  const rowsDeMensaje = (msg) => {
    if (msg.chart?.data?.length) return msg.chart.data;
    if (Array.isArray(msg.rawData)) {
      return msg.rawData.flatMap(r => r.rows || []);
    }
    return [];
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>Asistente {process.env.REACT_APP_NOMBRE_INSTITUCION || ''}</div>

      <div style={styles.messagesArea}>
        {loadingConv && <p style={{ textAlign: 'center', color: '#888', fontSize: '13px' }}>Cargando conversación…</p>}
        {!loadingConv && messages.length === 0 && (
          <div style={styles.msgBot}>¡Hola! Soy el asistente de Sistemify. Pregúntame sobre saldos de clientes, cargos, facturación o reparto de utilidades entre socios. También puedo generar gráficas.</div>
        )}
        {messages.map((msg, index) => {
          const rows = rowsDeMensaje(msg);
          return (
            <div key={index}>
              <div style={msg.role === 'user' ? styles.msgUser : styles.msgBot}>
                {msg.role === 'user' ? <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div> : <RespuestaMarkdown texto={msg.text} />}
                {msg.chart && <ChartRenderer config={msg.chart} />}
              </div>

              {msg.role === 'assistant' && rows.length > 0 && (
                <div style={{ marginTop: '5px', marginBottom: '10px', display: 'flex', gap: '8px', paddingLeft: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => downloadPDF(msg.text, rows, 'reporte')}
                    style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                    title="Descargar como PDF"
                  >
                    📄 PDF
                  </button>
                  <button
                    onClick={() => downloadExcel(rows, 'datos')}
                    style={{ backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
                    title="Descargar como Excel"
                  >
                    📊 Excel
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {isLoading && <div style={{ ...styles.msgBot, fontStyle: 'italic', color: '#666' }}>Analizando datos...</div>}
        <div ref={messagesEndRef} />
      </div>

      <form style={styles.inputArea} onSubmit={handleSend}>
        <textarea
          ref={inputRef}
          style={styles.input}
          value={input}
          onChange={handleInputChange}
          placeholder="Escribe tu pregunta..."
          disabled={isLoading}
          rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
        />
        <button type="submit" disabled={isLoading} style={{ cursor: 'pointer', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 15px' }}>
          {isLoading ? '...' : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}
